import React, { memo } from 'react';
import {
  Key, Laptop, Globe, Layers, Database, Users, Settings, Shield, ShieldCheck, ShieldAlert,
  CheckCircle, Activity, Package, Lock, UserCheck, Server, GitBranch, Grid, EyeOff, Tag,
  Search, Archive, Box, LogIn, Code, FileText, Smartphone, Edit2
} from 'lucide-react';
import { CANVAS_DIMENSIONS, NODE_DIMENSIONS } from '../data/maturityDiagramTemplates';

/**
 * Icon mapping for node types
 */
const ICON_MAP = {
  key: Key,
  laptop: Laptop,
  globe: Globe,
  layers: Layers,
  database: Database,
  users: Users,
  settings: Settings,
  shield: Shield,
  'shield-check': ShieldCheck,
  'shield-alert': ShieldAlert,
  'check-circle': CheckCircle,
  activity: Activity,
  package: Package,
  lock: Lock,
  'user-check': UserCheck,
  server: Server,
  'git-branch': GitBranch,
  grid: Grid,
  'eye-off': EyeOff,
  tag: Tag,
  search: Search,
  archive: Archive,
  box: Box,
  'log-in': LogIn,
  code: Code,
  'file-text': FileText,
  smartphone: Smartphone,
  edit2: Edit2
};

/**
 * Grid size for dot pattern
 */
const GRID_SIZE = 20;

/**
 * JourneyDiagramCanvas Component
 *
 * Renders an architecture diagram for a maturity level.
 * Shows nodes, edges, and optional annotations.
 */
const JourneyDiagramCanvas = memo(function JourneyDiagramCanvas({
  template,
  showAnnotations = true,
  highlightNew = true,
  width = CANVAS_DIMENSIONS.width,
  height = CANVAS_DIMENSIONS.height
}) {
  if (!template) {
    return (
      <div className="ztj-empty-state" style={{ minHeight: 300 }}>
        <Layers size={48} />
        <p>No diagram available</p>
      </div>
    );
  }

  const { nodes = [], edges = [], annotations = [] } = template;

  return (
    <div
      className="ztj-canvas"
      style={{ width: '100%', height: height }}
    >
      <div
        className="ztj-canvas__viewport"
        style={{
          width,
          height,
          position: 'relative'
        }}
      >
        {/* Grid background */}
        <svg
          className="ztj-canvas__grid"
          width={width}
          height={height}
        >
          <defs>
            <pattern
              id="journey-grid-pattern"
              width={GRID_SIZE}
              height={GRID_SIZE}
              patternUnits="userSpaceOnUse"
            >
              <circle
                cx={GRID_SIZE / 2}
                cy={GRID_SIZE / 2}
                r="1"
                fill="var(--color-border, #e4e4e7)"
                opacity="0.4"
              />
            </pattern>
          </defs>
          <rect
            width={width}
            height={height}
            fill="url(#journey-grid-pattern)"
          />
        </svg>

        {/* Edges (SVG layer) */}
        <svg
          className="ztj-canvas__edges"
          width={width}
          height={height}
          style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
        >
          {edges.map(edge => {
            const sourceNode = nodes.find(n => n.id === edge.source);
            const targetNode = nodes.find(n => n.id === edge.target);

            if (!sourceNode || !targetNode) return null;

            // Calculate anchor points
            const sourceWidth = sourceNode.isInput || sourceNode.isOutput
              ? NODE_DIMENSIONS.inputWidth
              : NODE_DIMENSIONS.width;
            const sourceHeight = sourceNode.isInput || sourceNode.isOutput
              ? NODE_DIMENSIONS.inputHeight
              : NODE_DIMENSIONS.height;
            const targetWidth = targetNode.isInput || targetNode.isOutput
              ? NODE_DIMENSIONS.inputWidth
              : NODE_DIMENSIONS.width;
            const targetHeight = targetNode.isInput || targetNode.isOutput
              ? NODE_DIMENSIONS.inputHeight
              : NODE_DIMENSIONS.height;

            const x1 = sourceNode.x + sourceWidth;
            const y1 = sourceNode.y + sourceHeight / 2;
            const x2 = targetNode.x;
            const y2 = targetNode.y + targetHeight / 2;

            // Calculate control points for a smooth curve
            const dx = x2 - x1;
            const cx1 = x1 + dx * 0.4;
            const cx2 = x2 - dx * 0.4;

            const isNew = highlightNew && edge.isNew;
            const strokeColor = isNew ? 'var(--color-success, #10b981)' : 'var(--color-text-subtle, #a1a1aa)';
            const strokeWidth = isNew ? 2.5 : 2;
            const strokeDasharray = edge.style === 'dashed' ? '6 4' : edge.style === 'dotted' ? '2 2' : 'none';

            return (
              <g key={edge.id}>
                {/* Edge path */}
                <path
                  d={`M ${x1} ${y1} C ${cx1} ${y1}, ${cx2} ${y2}, ${x2} ${y2}`}
                  fill="none"
                  stroke={strokeColor}
                  strokeWidth={strokeWidth}
                  strokeDasharray={strokeDasharray}
                  strokeLinecap="round"
                />
                {/* Arrow head */}
                <polygon
                  points={`${x2},${y2} ${x2-8},${y2-4} ${x2-8},${y2+4}`}
                  fill={strokeColor}
                />
                {/* Edge label */}
                {edge.label && (
                  <text
                    x={(x1 + x2) / 2}
                    y={(y1 + y2) / 2 - 8}
                    textAnchor="middle"
                    fill="var(--color-text-muted, #71717a)"
                    fontSize="10"
                    fontWeight="500"
                  >
                    {edge.label}
                  </text>
                )}
              </g>
            );
          })}
        </svg>

        {/* Nodes (HTML layer) */}
        <div
          className="ztj-canvas__nodes"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width,
            height
          }}
        >
          {nodes.map(node => {
            const IconComponent = ICON_MAP[node.icon] || Layers;
            const isNew = highlightNew && node.isNew;
            const isHighlight = node.isHighlight;
            const isSpecial = node.isInput || node.isOutput;

            const nodeWidth = isSpecial ? NODE_DIMENSIONS.inputWidth : NODE_DIMENSIONS.width;
            const nodeHeight = isSpecial ? NODE_DIMENSIONS.inputHeight : NODE_DIMENSIONS.height;

            return (
              <div
                key={node.id}
                className={`ztj-node ${isNew ? 'ztj-node--new' : ''} ${isHighlight ? 'ztj-node--highlight' : ''} ${isSpecial ? 'ztj-node--input' : ''}`}
                style={{
                  left: node.x,
                  top: node.y,
                  width: nodeWidth,
                  minHeight: nodeHeight
                }}
              >
                <div className="ztj-node__icon">
                  <IconComponent size={isSpecial ? 20 : 22} />
                </div>
                <div className="ztj-node__label">{node.label}</div>
                {node.category && !isSpecial && (
                  <div className="ztj-node__category">{node.category}</div>
                )}
              </div>
            );
          })}
        </div>

        {/* Annotations (overlay) */}
        {showAnnotations && annotations.length > 0 && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width,
              height,
              pointerEvents: 'none'
            }}
          >
            {annotations.map(anno => (
              <div
                key={anno.id}
                className={`ztj-annotation ztj-annotation--${anno.type || 'info'}`}
                style={{
                  left: anno.x,
                  top: anno.y
                }}
              >
                {anno.text}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

export default JourneyDiagramCanvas;
